import { morph } from '@theme/morph';
import { onProductSelect } from '@theme/pro-product';

/**
 * Horizon Pro — product media gallery (snippets/pro-media-gallery.liquid).
 * Scroll-snap slider with arrows, dots, counter and thumbnails; stacked / 2-column layouts on desktop;
 * video players; lightbox; follows the selected variant.
 */

const DESKTOP_QUERY = window.matchMedia('(min-width: 750px)');

class ProMediaGallery extends HTMLElement {
  #index = 0;
  /** @type {() => void} */
  #unsubscribe = () => {};
  /** @type {IntersectionObserver | null} */
  #videoObserver = null;
  #scrollFrame = 0;

  connectedCallback() {
    this.#bind();
    this.#unsubscribe = onProductSelect(this, (detail) => this.#onVariantChange(detail));
    DESKTOP_QUERY.addEventListener('change', this.#onBreakpointChange);
  }

  disconnectedCallback() {
    this.#unsubscribe();
    this.#videoObserver?.disconnect();
    this.track?.removeEventListener('scroll', this.#onScroll);
    this.thumbsList?.removeEventListener('scroll', this.#updateThumbArrows);
    DESKTOP_QUERY.removeEventListener('change', this.#onBreakpointChange);
  }

  get track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-pro-track]'));
  }

  get slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.track?.children ?? []));
  }

  get thumbsList() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-pro-thumbs]'));
  }

  get thumbItems() {
    return /** @type {HTMLElement[]} */ (Array.from(this.thumbsList?.children ?? []));
  }

  /** True when the main media is a slider (not stacked / 2 columns on desktop) */
  get isSlider() {
    if (!DESKTOP_QUERY.matches) return true;
    return this.classList.contains('pro-gallery--d-thumbnail') || this.classList.contains('pro-gallery--d-thumbnail_slider');
  }

  #bind() {
    this.addEventListener('click', this.#onClick);
    this.track?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.thumbsList?.addEventListener('scroll', this.#updateThumbArrows, { passive: true });
    this.#setupVideos();
    this.#updateUi(0);
    requestAnimationFrame(this.#updateThumbArrows);
  }

  /* ---------- Navigation ---------- */

  /**
   * @param {number} index
   * @param {boolean} [smooth]
   */
  goTo(index, smooth = true) {
    const slides = this.slides;
    if (slides.length === 0) return;
    const clamped = Math.max(0, Math.min(index, slides.length - 1));
    const slide = slides[clamped];
    const track = this.track;
    if (!slide || !track) return;

    if (this.isSlider) {
      const padding = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
      track.scrollTo({ left: slide.offsetLeft - padding, behavior: smooth ? 'smooth' : 'instant' });
    }
    this.#updateUi(clamped);
  }

  #onScroll = () => {
    if (!this.isSlider) return;
    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => {
      const track = this.track;
      if (!track) return;
      const padding = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
      const start = track.scrollLeft + padding;
      let closest = 0;
      let distance = Infinity;
      this.slides.forEach((slide, i) => {
        const d = Math.abs(slide.offsetLeft - start);
        if (d < distance) {
          distance = d;
          closest = i;
        }
      });
      if (closest !== this.#index) this.#updateUi(closest);
    });
  };

  #onBreakpointChange = () => {
    this.goTo(this.#index, false);
  };

  /** @param {number} index */
  #updateUi(index) {
    this.#index = index;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));

    for (const dots of this.querySelectorAll('.pro-gallery__dots')) {
      Array.from(dots.children).forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }

    const counter = this.querySelector('[data-pro-counter-current]');
    if (counter) counter.textContent = String(index + 1);

    const last = this.slides.length - 1;
    for (const prev of this.querySelectorAll('[data-pro-prev]')) /** @type {HTMLButtonElement} */ (prev).disabled = index <= 0;
    for (const next of this.querySelectorAll('[data-pro-next]')) /** @type {HTMLButtonElement} */ (next).disabled = index >= last;

    this.thumbItems.forEach((item, i) => {
      const button = item.querySelector('[data-pro-thumb]');
      button?.classList.toggle('is-active', i === index);
      if (i === index) button?.setAttribute('aria-current', 'true');
      else button?.removeAttribute('aria-current');
    });
    this.#scrollThumbIntoView(index);
    this.#syncVideos();
  }

  /** @param {number} index */
  #scrollThumbIntoView(index) {
    const list = this.thumbsList;
    const item = this.thumbItems[index];
    if (!list || !item) return;
    const vertical = list.scrollHeight > list.clientHeight + 1 && list.scrollWidth <= list.clientWidth + 1;
    if (vertical) {
      const top = item.offsetTop;
      if (top < list.scrollTop || top + item.offsetHeight > list.scrollTop + list.clientHeight) {
        list.scrollTo({ top: top - (list.clientHeight - item.offsetHeight) / 2, behavior: 'smooth' });
      }
    } else {
      const left = item.offsetLeft;
      if (left < list.scrollLeft || left + item.offsetWidth > list.scrollLeft + list.clientWidth) {
        list.scrollTo({ left: left - (list.clientWidth - item.offsetWidth) / 2, behavior: 'smooth' });
      }
    }
  }

  /** Thumbnail arrows are disabled at the start and the end of the list, like Shrine */
  #updateThumbArrows = () => {
    const list = this.thumbsList;
    if (!list) return;
    const vertical = list.scrollHeight > list.clientHeight + 1 && list.scrollWidth <= list.clientWidth + 1;
    const position = vertical ? list.scrollTop : Math.abs(list.scrollLeft);
    const max = vertical ? list.scrollHeight - list.clientHeight : list.scrollWidth - list.clientWidth;
    this.querySelector('[data-pro-thumbs-prev]')?.toggleAttribute('disabled', position <= 1);
    this.querySelector('[data-pro-thumbs-next]')?.toggleAttribute('disabled', position >= max - 1);
  };

  /** @param {number} direction */
  #scrollThumbs(direction) {
    const list = this.thumbsList;
    if (!list) return;
    const vertical = list.scrollHeight > list.clientHeight + 1 && list.scrollWidth <= list.clientWidth + 1;
    if (vertical) list.scrollBy({ top: direction * list.clientHeight * 0.8, behavior: 'smooth' });
    else list.scrollBy({ left: direction * list.clientWidth * 0.8, behavior: 'smooth' });
  }

  /* ---------- Clicks ---------- */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-pro-prev]')) return this.goTo(this.#index - 1);
    if (target.closest('[data-pro-next]')) return this.goTo(this.#index + 1);
    if (target.closest('[data-pro-thumbs-prev]')) return this.#scrollThumbs(-1);
    if (target.closest('[data-pro-thumbs-next]')) return this.#scrollThumbs(1);

    const thumb = target.closest('[data-pro-thumb]');
    if (thumb) {
      const item = thumb.closest('.pro-gallery__thumb-item');
      const index = this.thumbItems.indexOf(/** @type {HTMLElement} */ (item));
      if (index === -1) return;
      if (this.isSlider) this.goTo(index);
      else this.slides[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (target.closest('[data-pro-open-lightbox]')) {
      const slide = target.closest('.pro-gallery__slide');
      return this.#openLightbox(this.slides.indexOf(/** @type {HTMLElement} */ (slide)));
    }

    if (target.closest('[data-pro-video-play]')) {
      const media = target.closest('[data-pro-video], [data-pro-external-video]');
      if (media) this.#playMedia(/** @type {HTMLElement} */ (media));
      return;
    }
    if (target.closest('[data-pro-video-toggle]')) {
      const video = target.closest('[data-pro-video]')?.querySelector('video');
      const container = target.closest('[data-pro-video]');
      if (video && container) {
        if (video.paused) {
          container.classList.remove('is-user-paused');
          video.play().catch(() => {});
        } else {
          container.classList.add('is-user-paused');
          video.pause();
        }
      }
      return;
    }
    if (target.closest('[data-pro-video-sound]')) {
      const video = target.closest('[data-pro-video]')?.querySelector('video');
      if (video) video.muted = !video.muted;
      return;
    }
    if (target.closest('[data-pro-model-load]')) {
      const media = target.closest('[data-pro-model]');
      if (media) this.#loadModel(/** @type {HTMLElement} */ (media));
      return;
    }

    // Clicking a playing "play button" video pauses it
    const playing = target.closest('.pro-media--player-play_btn.is-playing');
    if (playing && target.tagName === 'VIDEO') {
      /** @type {HTMLVideoElement} */ (target).pause();
    }
  };

  /* ---------- Variant change ---------- */

  /** @param {any} detail */
  #onVariantChange(detail) {
    if (this.dataset.rerender === 'true') {
      const fresh = detail.html.querySelector(`pro-media-gallery#${CSS.escape(this.id)}`);
      if (fresh) {
        this.track?.removeEventListener('scroll', this.#onScroll);
        morph(this, fresh);
        this.track?.addEventListener('scroll', this.#onScroll, { passive: true });
        this.#setupVideos();
        this.#index = 0;
        if (this.track) this.track.scrollLeft = 0;
        this.#updateUi(0);
      }
      return;
    }

    const mediaId = detail.resource?.featured_media?.id;
    if (!mediaId) return;
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index === -1) return;

    if (this.dataset.prepend === 'true' && index > 0) {
      this.#moveToFront(String(mediaId));
      if (this.track) this.track.scrollLeft = 0;
      this.goTo(0, false);
      if (!this.isSlider) this.slides[0]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
      this.goTo(index);
    }
  }

  /** @param {string} mediaId */
  #moveToFront(mediaId) {
    const selector = `[data-media-id="${CSS.escape(mediaId)}"]`;
    for (const list of [this.track, this.thumbsList, this.querySelector('[data-pro-lightbox-track]')]) {
      const item = list?.querySelector(`:scope > ${selector}`);
      if (list && item) list.prepend(item);
    }
  }

  /* ---------- Videos ---------- */

  #setupVideos() {
    this.#videoObserver?.disconnect();
    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const container = /** @type {HTMLElement} */ (entry.target);
          container.dataset.inView = String(entry.isIntersecting);
        }
        this.#syncVideos();
      },
      { threshold: 0.4 }
    );

    for (const container of this.querySelectorAll('[data-pro-video]')) {
      const video = container.querySelector('video');
      if (!video) continue;
      this.#videoObserver.observe(container);
      if (video.dataset.proBound) continue;
      video.dataset.proBound = 'true';

      video.addEventListener('play', () => container.classList.add('is-playing'));
      video.addEventListener('pause', () => container.classList.remove('is-playing'));
      video.addEventListener('volumechange', () => container.classList.toggle('is-unmuted', !video.muted));
      video.addEventListener('ended', () => container.classList.remove('is-playing'));

      const timeline = container.querySelector('[data-pro-video-timeline]');
      if (timeline instanceof HTMLElement) {
        const bar = /** @type {HTMLElement | null} */ (timeline.firstElementChild);
        video.addEventListener('timeupdate', () => {
          if (bar && video.duration) bar.style.width = `${(video.currentTime / video.duration) * 100}%`;
        });
        timeline.addEventListener('pointerdown', (event) => {
          const seek = (/** @type {PointerEvent} */ e) => {
            const rect = timeline.getBoundingClientRect();
            const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
            if (video.duration) video.currentTime = ratio * video.duration;
          };
          seek(event);
          timeline.setPointerCapture(event.pointerId);
          timeline.addEventListener('pointermove', seek);
          timeline.addEventListener('pointerup', () => timeline.removeEventListener('pointermove', seek), { once: true });
        });
      }
    }
  }

  /** Plays the autoplay video of the active slide and pauses all other videos */
  #syncVideos() {
    this.slides.forEach((slide, i) => {
      const active = i === this.#index || !this.isSlider;
      const container = /** @type {HTMLElement | null} */ (slide.querySelector('[data-pro-video]'));
      const video = container?.querySelector('video');
      if (container && video) {
        const autoplay = container.dataset.player === 'autoplay';
        const visible = container.dataset.inView !== 'false';
        if (autoplay && active && visible && !container.classList.contains('is-user-paused')) {
          video.play().catch(() => {});
        } else if (!active || !visible) {
          video.pause();
        }
      }
      if (i !== this.#index && this.isSlider) {
        const iframe = slide.querySelector('[data-pro-external-video] iframe');
        if (iframe) iframe.remove();
        slide.querySelector('[data-pro-external-video]')?.classList.remove('is-playing');
      }
    });
  }

  /** @param {HTMLElement} media */
  #playMedia(media) {
    const player = media.dataset.player;

    if (player === 'modal') {
      const template = media.querySelector('template[data-pro-video-template]');
      const source = media.hasAttribute('data-pro-external-video') ? this.#externalIframe(media) : template?.content.cloneNode(true);
      if (source) this.#openVideoModal(source);
      return;
    }

    if (media.hasAttribute('data-pro-external-video')) {
      if (!media.querySelector('iframe')) media.appendChild(this.#externalIframe(media));
      media.classList.add('is-playing');
      return;
    }

    const video = media.querySelector('video');
    if (!video) return;
    video.muted = false;
    video.play().catch(() => {
      video.muted = true;
      video.play().catch(() => {});
    });
  }

  /** @param {HTMLElement} media */
  #externalIframe(media) {
    const iframe = document.createElement('iframe');
    iframe.src = media.dataset.src || '';
    iframe.title = media.dataset.title || '';
    iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.className = 'pro-media__iframe';
    return iframe;
  }

  /** @param {Node} content */
  #openVideoModal(content) {
    const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-pro-video-modal]'));
    const holder = dialog?.querySelector('[data-pro-video-modal-content]');
    if (!dialog || !holder) return;
    holder.replaceChildren(content);
    dialog.showModal();
    holder.querySelector('video')?.play().catch(() => {});

    const close = () => {
      holder.replaceChildren();
      dialog.close();
    };
    dialog.querySelector('[data-pro-video-modal-close]')?.addEventListener('click', close, { once: true });
    dialog.addEventListener('close', () => holder.replaceChildren(), { once: true });
  }

  /** @param {HTMLElement} media */
  #loadModel(media) {
    const template = media.querySelector('template[data-pro-model-template]');
    if (!template || media.querySelector('model-viewer')) return;
    media.appendChild(template.content.cloneNode(true));
    media.classList.add('is-playing');
    // @ts-ignore Shopify global
    window.Shopify?.loadFeatures?.([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: () => {
          const viewer = media.querySelector('model-viewer');
          // @ts-ignore Shopify global
          if (viewer && window.Shopify?.ModelViewerUI) new window.Shopify.ModelViewerUI(viewer);
        },
      },
    ]);
  }

  /* ---------- Lightbox ---------- */

  /** @param {number} index */
  #openLightbox(index) {
    const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-pro-lightbox]'));
    const track = /** @type {HTMLElement | null} */ (dialog?.querySelector('[data-pro-lightbox-track]'));
    if (!dialog || !track) return;

    dialog.showModal();
    const slide = /** @type {HTMLElement | undefined} */ (track.children[Math.max(index, 0)]);
    if (slide) track.scrollLeft = slide.offsetLeft;

    if (dialog.dataset.proBound) return;
    dialog.dataset.proBound = 'true';

    const step = (/** @type {number} */ direction) => track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
    dialog.querySelector('[data-pro-lightbox-prev]')?.addEventListener('click', () => step(-1));
    dialog.querySelector('[data-pro-lightbox-next]')?.addEventListener('click', () => step(1));
    dialog.querySelector('[data-pro-lightbox-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') step(-1);
      if (event.key === 'ArrowRight') step(1);
    });
  }
}

if (!customElements.get('pro-media-gallery')) customElements.define('pro-media-gallery', ProMediaGallery);
